// Surface 06 — les sites surveillés.
//
// L'ajout par URL est le moment le plus démonstratif de la console : on colle
// une URL, le système reconnaît la plateforme, teste RÉELLEMENT l'extraction et
// montre un aperçu des intitulés trouvés avant qu'on ajoute quoi que ce soit.
//
// ET QUAND ÇA NE MARCHE PAS. C'était une impasse jusqu'au 2026-09-27 : « pas
// encore prise en charge », point final. Or la moitié du web de l'emploi est
// dans ce cas et la plupart de ces sites s'ajoutent en une ligne de
// configuration, donc le refus disait « ce produit ne marche pas » là où il
// aurait dû dire « il faut que quelqu'un y passe une minute ». D'où le bouton de
// demande, la liste des demandes en attente, et le fait de DIRE le délai : une
// attente annoncée n'est pas une panne (lot C, docs/chantier-partage-radar.md).

import { useState } from 'react'
import { Plus, Search, Link2, CircleCheck, CircleAlert, Pencil, Trash2, Undo2, MailQuestion, Clock, Eye, EyeOff, Upload, RefreshCw } from 'lucide-react'
import { get, post, type SourceRow } from '../lib/api'
import { relTime, fullDate } from '../lib/format'
import { useMots } from '../lib/vocabulaire'
import { navigate } from '../lib/router'
import { useData, Loading, Empty, Modal, ActionButton, useToast } from '../components/common'
import { useT } from '../lib/i18n'
import { useFormule } from '../lib/formule'
import { signalerTache } from '../components/bandeauTaches'
import { GrilleSites, LogoEntreprise, jourDeLecture } from '../components/sites'
import { LigneEmp, LignePage, noterSuivi, type LigneEmployeur, type PageEnAttente } from '../components/employeurs'
import { BoutonFiltre, useSuivreSite } from '../components/filtreSite'

interface DemandeRow {
  id: number
  url: string
  status: 'en_attente' | 'active' | 'refusee'
  createdAt: string
  notifiedAt: string | null
}

/** Ce que la colonne « santé » dit d'une source. En Radar c'est « je la suis
 *  ou pas » : la pause y est un geste d'exploitant, invisible à l'écran. */
function sante(s: SourceRow, radar: boolean, t: ReturnType<typeof useT>): { cls: string; label: string } {
  // En Radar, un état qu'un particulier lit sans traduction : ça marche, ou
  // c'est en panne depuis telle date. Le message technique de l'erreur et le
  // suivi (déjà dit par le bouton de la ligne) n'y ont pas leur place.
  if (radar) {
    // Suivi depuis peu, pas encore relu (issue #131) : ses offres ont l'âge
    // de la lecture faite quand personne ne le suivait.
    if (s.status !== 'error' && s.a_relire) {
      return { cls: 'vieux', label: t('sources.staleState', { date: jourDeLecture(s.last_checked_at) }) }
    }
    if (s.status !== 'error') return { cls: 'ok', label: t('sources.stateOk') }
    return {
      cls: 'err',
      label: s.error_since ? t('sources.brokenSince', { date: fullDate(s.error_since) }) : t('sources.broken'),
    }
  }
  if (s.status === 'error') {
    return { cls: 'err', label: t('sources.errorLabel', { detail: String(s.last_error ?? '').slice(0, 50) }) }
  }
  return s.status === 'paused' ? { cls: 'pause', label: t('sources.paused') } : { cls: 'ok', label: t('sources.active') }
}

interface Detection {
  recognized: boolean
  unreachable?: boolean
  detail?: string
  platforms?: string[]
  platform?: string
  how?: 'url' | 'html'
  url?: string
  count?: number
  preview?: string[]
}

/** Ce qu'on dit en se mettant à suivre un site. S'il n'avait aucun suiveur, ses
 *  offres datent de sa dernière lecture et il est relu maintenant (issue #131) :
 *  le dire, sinon elles passeraient pour fraîches. */
function messageSuivi(s: SourceRow, relu: boolean, t: ReturnType<typeof useT>): string {
  const name = s.company ?? s.name
  return relu
    ? t('sources.followedStale', { name, date: jourDeLecture(s.last_checked_at) })
    : t('sources.followed', { name })
}

/** Le seul geste d'une ligne en Radar : suivre ou ne plus suivre. */
function SuivreSource({ source: s, onDone }: { source: SourceRow; onDone: () => void }) {
  const t = useT()
  const toast = useToast()
  const nom = s.company ?? s.name
  // Un gros site ouvre la fenêtre de filtre (issue #191).
  const { suivre, fenetre } = useSuivreSite()
  return (
    <>
      <ActionButton
        size="sm"
        variant={s.abonne ? 'secondary' : 'primary'}
        onClick={async () => {
          if (s.abonne) {
            await post(`/api/sources/${s.id}/abonnement`, { abonne: false })
            toast(t('sources.unfollowed', { name: nom }))
          } else {
            const r = await suivre(s.id, nom)
            if (!r) return
            signalerTache()
            noterSuivi(s.id)
            toast(messageSuivi(s, Boolean(r.relu), t))
          }
          onDone()
        }}
      >
        {s.abonne ? <><EyeOff size={13} />{t('sources.unfollow')}</> : <><Eye size={13} />{t('sources.follow')}</>}
      </ActionButton>
      {fenetre}
    </>
  )
}

/**
 * Les sites déjà surveillés par Radar et que ce compte ne suit pas encore.
 * Replié par défaut, avec une recherche : c'est un catalogue où l'on vient
 * piocher, pas la liste de ce qu'on suit.
 */
function CatalogueSites({ sites, ouvert, onDone }: { sites: SourceRow[]; ouvert: boolean; onDone: () => void }) {
  const t = useT()
  const toast = useToast()
  const { suivre, fenetre } = useSuivreSite()
  if (sites.length === 0) return null
  return (
    <details className="card catalogue" open={ouvert}>
      {fenetre}
      <summary>
        <Search size={15} /> {t('sources.browseTitle', { n: sites.length })}
      </summary>
      <p className="field-help">{t('sources.browseHelp')}</p>
      {/* Une tuile touchée se suit tout de suite et quitte le catalogue pour
          le tableau des sites suivis : pas de « valider » à oublier. */}
      <GrilleSites
        sites={sites}
        choisi={() => false}
        onBasculer={async (s) => {
          // Le refus (plafond de pages suivies) doit se lire : sans ce `catch`,
          // la tuile revenait à son état sans un mot.
          let r
          try {
            r = await suivre(s.id, s.company ?? s.name)
          } catch (e: any) {
            toast(e?.message ?? String(e), 'err')
            return
          }
          if (!r) return
          signalerTache()
          noterSuivi(s.id)
          toast(messageSuivi(s, Boolean(r.relu), t))
          onDone()
        }}
      />
    </details>
  )
}

/** Les gestes de l'exploitant sur une ligne, en console : corriger, couper, retirer. */
function ActionsExploitant({
  source: s,
  onEditer,
  onRetirer,
  onDone,
}: {
  source: SourceRow
  onEditer: (s: SourceRow) => void
  onRetirer: (s: SourceRow) => void
  onDone: () => void
}) {
  const t = useT()
  const toast = useToast()
  const enPause = s.status === 'paused'
  return (
    <>
      <button className="chip" onClick={() => onEditer(s)}>
        <Pencil size={13} />{t('common.update')}
      </button>
      <ActionButton
        size="sm"
        onClick={async () => {
          await post(`/api/sources/${s.id}/pause`, { paused: !enPause })
          toast(enPause ? t('sources.reactivatedToast') : t('sources.pausedToast'))
          onDone()
        }}
      >
        {enPause ? t('sources.resume') : t('sources.pause')}
      </ActionButton>
      <button className="chip chip-danger" onClick={() => onRetirer(s)} title={t('sources.removeTitle')}>
        <Trash2 size={13} />
      </button>
    </>
  )
}

export function Sources({ onChanged, plafond }: { onChanged: () => void; plafond?: number | null }) {
  const t = useT()
  const mots = useMots()
  // En Radar, une source est COMMUNE : la couper couperait la veille des
  // autres. Le geste personnel est l'abonnement, et les gestes d'exploitant
  // (pause, retrait, correction) n'ont rien à faire à l'écran (lot G).
  const radar = useFormule() === 'radar'
  const sources = useData(() => get<SourceRow[]>('/api/sources?all=1'), [])
  const [edition, setEdition] = useState<SourceRow | null>(null)
  const [retrait, setRetrait] = useState<SourceRow | null>(null)
  const [url, setUrl] = useState('')
  const [detection, setDetection] = useState<Detection | null>(null)
  const [detecting, setDetecting] = useState(false)
  // Refus pour cause de doublon : gardé À PART du toast, parce qu'il appelle
  // une décision et non un simple accusé de réception. Un message qui disparaît
  // au bout de trois secondes ne peut pas porter un « ajouter quand même ».
  const [doublon, setDoublon] = useState<string | null>(null)
  // Les demandes en attente, à côté des sources : c'est le même sujet vu de
  // l'autre côté, « ce que je surveille » et « ce que j'attends ».
  const demandes = useData(() => get<DemandeRow[]>('/api/source-requests'), [])
  // Radar (lot 3) : l'état de chaque page en mots, et les pages demandées.
  const emp = useData(
    () => (radar ? get<{ suivis: LigneEmployeur[]; pages: PageEnAttente[] }>('/api/radar/employeurs') : Promise.resolve(null)),
    [radar]
  )
  const [note, setNote] = useState('')
  const toast = useToast()

  /** Demander l'activation d'un site que la détection ne reconnaît pas. */
  const demander = async () => {
    try {
      const r = await post<DemandeRow & { echecsMail?: { adresse: string }[]; delaiJours?: number }>(
        '/api/source-requests',
        { url, note }
      )
      // Un e-mail qui n'est pas parti se DIT. Annoncer « vous recevrez un
      // message » alors que l'envoi a échoué, c'est faire attendre pour rien.
      if (r.echecsMail && r.echecsMail.length > 0) {
        toast(t('sources.requestSentNoMail'), 'err')
      } else {
        toast(t('sources.requestSent', { n: r.delaiJours ?? 7 }))
      }
      setUrl('')
      setNote('')
      setDetection(null)
      demandes.reload()
    } catch (e: any) {
      toast(e?.message ?? t('sources.requestFailed'), 'err')
    }
  }

  /** Ajout, éventuellement en passant outre le contrôle de doublon. */
  const ajouter = async (force: boolean) => {
    try {
      const r = await post<{ name: string; inserted: number; dejaConnu?: boolean }>('/api/sources', { url, force })
      toast(r.dejaConnu ? t('sources.alreadyKnownToast', { name: r.name }) : t('sources.addedToast', { name: r.name, n: r.inserted }))
      signalerTache()
      setUrl('')
      setDetection(null)
      setDoublon(null)
      onChanged()
      sources.reload()
    } catch (e: any) {
      if (e?.code === 'doublon') setDoublon(e.message)
      else toast(e.message, 'err')
    }
  }

  if (sources.loading) return <Loading label={t('sources.loading')} />
  if (sources.error) return <Empty>{sources.error}</Empty>

  // Les sources d'APPORT (saisie manuelle, extension) ne se suivent ni ne se
  // coupent : elles ne portent que ce que chaque compte y pose lui-même.
  const toutes = (sources.data ?? []).filter((s) => s.platform !== 'manual' && s.platform !== 'extension')
  // En Radar, le tableau ne montre que les sites SUIVIS. Le catalogue commun se
  // parcourt à part (`CatalogueSites`) : mélangés, trente sites non cochés
  // se lisaient « on m'a abonné à tout ça ».
  const nonArchivees = toutes.filter((s) => !s.archived_at)
  const rows = radar ? nonArchivees.filter((s) => s.abonne) : nonArchivees
  const disponibles = radar ? nonArchivees.filter((s) => !s.abonne) : []
  const archivees = toutes.filter((s) => s.archived_at)
  const actives = rows.filter((s) => s.status === 'active').length
  const erreurs = rows.filter((s) => s.status === 'error').length
  const enAttente = (demandes.data ?? []).filter((d) => d.status === 'en_attente')

  function rafraichir() {
    sources.reload()
    emp.reload()
    onChanged()
  }

  async function detecter() {
    if (!url.trim()) return
    setDetecting(true)
    setDetection(null)
    try {
      setDetection(await post<Detection>('/api/sources/detect', { url }))
    } catch (e: any) {
      setDetection({ recognized: false, unreachable: true, detail: e.message })
    } finally {
      setDetecting(false)
    }
  }

  return (
    <>
      <div className="bar">
        <div>
          {/* Le titre EST l'entrée du menu : « Sites suivis » au menu et
              « Sources » en tête de page désignaient le même écran. */}
          <h3>{mots('nav.sources')}</h3>
          <div className="sub">
            {radar ? (
              plafond === null ? t('emp.sousTitreSansMax', { n: rows.length }) : t('emp.sousTitre', { n: rows.length, max: plafond ?? 20 })
            ) : (
              <>
                {t('sources.activeSites', { n: actives })}{erreurs > 0 && ` · ${t('sources.errorCount', { n: erreurs })}`} · {t('sources.watchedNightly')}
              </>
            )}
          </div>
        </div>
      </div>

      <div className="body">
        <div className="addurl">
          <div className="ah"><Plus size={16} />{t('sources.addTitle')}</div>
          <div className="urlrow">
            <div className="urlin">
              <Link2 size={14} />
              <input
                type="url"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') detecter() }}
                placeholder={t('sources.urlPlaceholder')}
                aria-label={t('sources.urlAria')}
              />
            </div>
            <button className="btn-primary" onClick={detecter} disabled={detecting}>
              {detecting ? <span className="spinner" /> : <Search size={15} />}
              {t('sources.detect')}
            </button>
          </div>

          {detecting && <div className="loading">{t('sources.detecting')}</div>}

          {detection && !detection.recognized && (
            <div className="detected bad">
              <CircleAlert size={18} />
              <span className="dt2">
                {detection.unreachable ? (
                  <>{t('sources.unreachablePrefix')} <b>{t('sources.unreachable')}</b>{t('sources.unreachableSuffix')} {detection.detail}</>
                ) : (
                  <>
                    {t('sources.unsupportedPrefix')} <b>{t('sources.unsupported')}</b>{t('sources.unsupportedSuffix')}{' '}
                    {(detection.platforms ?? []).filter((p) => p !== 'manual').join(', ')}.
                  </>
                )}
                {/* La phrase qui manquait : ce n'est pas fini, voici ce qui se
                    passe ensuite, et voici combien de temps ça prend. */}
                <br />
                {t('sources.requestExplain')}
              </span>
            </div>
          )}

          {detection && !detection.recognized && (
            <div className="detected">
              <MailQuestion size={18} />
              <span className="dt2">
                <input
                  className="input-field"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder={t('sources.requestNotePlaceholder')}
                  aria-label={t('sources.requestNoteAria')}
                />
              </span>
              <div className="grow" />
              <ActionButton variant="primary" busyLabel={t('sources.requesting')} onClick={demander}>
                <MailQuestion size={15} />{t('sources.request')}
              </ActionButton>
            </div>
          )}

          {detection?.recognized && (
            <div className="detected">
              <CircleCheck size={18} />
              <span className="dt2">
                {t('sources.recognizedPrefix')} <b>{detection.platform}</b>
                {detection.how === 'html' && ` ${t('sources.viaHtml')}`} · <b>{t('sources.offersFound', { n: detection.count ?? 0 })}</b>
                {detection.preview?.length ? ` · ${t('sources.preview')}: ${detection.preview.map((x) => `« ${x} »`).join(', ')}` : ''}
                {detection.url && detection.url !== url && (
                  <><br />{t('sources.actuallyWatched')} <b>{detection.url}</b></>
                )}
              </span>
              <div className="grow" />
              <ActionButton variant="primary" busyLabel={t('sources.adding')} onClick={() => ajouter(false)}>
                <Plus size={15} />{t('sources.addToWatch')}
              </ActionButton>
            </div>
          )}

          {doublon && (
            <div className="detected bad">
              <CircleAlert size={18} />
              <span className="dt2">{doublon}</span>
              <div className="grow" />
              <button className="btn-secondary" onClick={() => setDoublon(null)}>{t('common.cancel')}</button>
              <ActionButton variant="primary" busyLabel={t('sources.adding')} onClick={() => ajouter(true)}>
                {t('sources.addAnyway')}
              </ActionButton>
            </div>
          )}
        </div>

        {!radar && enAttente.length > 0 && (
          <div className="card">
            <h3>
              <Clock size={16} /> {t('sources.pendingTitle')}
            </h3>
            <p className="field-help">{t('sources.pendingHelp')}</p>
            <ul className="pending">
              {enAttente.map((d) => (
                  <li key={d.id}>
                    <b>{d.url}</b>
                    {d.notifiedAt ? '' : ` · ${t('sources.pendingNoMail')}`}
                  </li>
                ))}
            </ul>
          </div>
        )}

        {radar && (
          <EmployeursRadar
            suivis={rows}
            disponibles={disponibles}
            etat={emp.data ?? null}
            plafond={plafond === undefined ? 20 : plafond}
            onDone={rafraichir}
          />
        )}

        {radar && <CatalogueSites sites={disponibles} ouvert={rows.length === 0} onDone={rafraichir} />}

        {radar ? null : (
          <div className="tblwrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>{t('sources.colCompany')}</th>
                  {/* La plateforme (`generic`, `teamtailor`…) est un détail
                      d'exploitant : en Radar, l'état dit seulement si ça marche. */}
                  {!radar && <th>{t('sources.colPlatform')}</th>}
                  <th>{radar ? t('sources.colState') : t('sources.colHealth')}</th>
                  <th>{t('sources.colLastCheck')}</th>
                  <th>{t('sources.colOffers')}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map((s) => (
                  <tr key={s.id}>
                    <td>
                      <div className="co-ligne">
                        <LogoEntreprise nom={s.company ?? s.name} logo={s.logo} />
                        <div>
                          <div className="co">{s.company ?? s.name}</div>
                          <div className="src-sub">{s.url.replace(/^https?:\/\//, '').slice(0, 46)}</div>
                        </div>
                      </div>
                    </td>
                    {!radar && <td><span className="src">{s.platform}</span></td>}
                    <td>
                      <span className={`health h-${sante(s, radar, t).cls}`}>
                        <span className="hd" />
                        {sante(s, radar, t).label}
                      </span>
                    </td>
                    <td><span className="when">{s.last_checked_at ? relTime(s.last_checked_at) : '—'}</span></td>
                    <td><span className="mono">{s.offers}</span></td>
                    <td className="srcact">
                      {radar ? (
                        <SuivreSource source={s} onDone={rafraichir} />
                      ) : (
                        <ActionsExploitant source={s} onEditer={setEdition} onRetirer={setRetrait} onDone={rafraichir} />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Les retirées restent listées, en bas et en retrait. « Supprimé »
            voulant dire « archivé » ici, les cacher complètement ferait
            croire à une vraie suppression et rendrait le retour impossible. */}
        {!radar && archivees.length > 0 && (
          <div className="arcsec">
            <div className="pool-h">{t('sources.removedTitle')}<span className="n">{archivees.length}</span></div>
            {archivees.map((s) => (
              <div className="krow" key={s.id}>
                <div className="grow">
                  <div className="co">{s.company ?? s.name}</div>
                  <div className="src-sub">
                    {s.platform} · {t('sources.removedAt', { when: relTime(s.archived_at) })}
                    {s.archived_offers > 0 && ` · ${t('sources.wouldComeBack', { n: s.archived_offers })}`}
                  </div>
                </div>
                <ActionButton
                  size="sm"
                  onClick={async () => {
                    const r = await post<{ offers: number }>(`/api/sources/${s.id}/archive`, { archived: false })
                    toast(t('sources.restoredToast', { name: s.company ?? s.name, n: r.offers }))
                    rafraichir()
                  }}
                >
                  <Undo2 size={13} />{t('sources.restoreToWatch')}
                </ActionButton>
              </div>
            ))}
          </div>
        )}
      </div>

      {edition && (
        <EditerSource source={edition} onClose={() => setEdition(null)} onDone={rafraichir} />
      )}
      {retrait && (
        <RetirerSource source={retrait} onClose={() => setRetrait(null)} onDone={rafraichir} />
      )}
    </>
  )
}

/** Combien de pages suggérées : trois, comme la maquette. Au-delà, c'est le
 *  catalogue, replié juste en dessous. */
const SUGGESTIONS = 3

/**
 * La page « Employeurs » de Radar (lot 3 de la refonte). Plus de tableau :
 * des employeurs nommés, chacun avec son état en mots, en quatre groupes.
 * Les pages suivies, les sites d'annonces à part (ils portent des centaines
 * d'employeurs), les pages demandées que Radar apprend à lire (hors plafond),
 * et trois suggestions tirées du catalogue, triées par offres dans vos métiers.
 */
function EmployeursRadar({
  suivis,
  disponibles,
  etat,
  plafond,
  onDone,
}: {
  suivis: SourceRow[]
  disponibles: SourceRow[]
  etat: { suivis: LigneEmployeur[]; pages: PageEnAttente[] } | null
  /** `null` : formule Complet, aucun plafond (#121). */
  plafond: number | null
  onDone: () => void
}) {
  const t = useT()
  const { changer, fenetre } = useSuivreSite()
  const parId = new Map(suivis.map((s) => [s.id, s]))
  const lignes = (etat?.suivis ?? []).filter((e) => parId.has(e.id))
  const employeurs = lignes.filter((e) => !e.annonces)
  const annonces = lignes.filter((e) => e.annonces)
  const pages = etat?.pages ?? []
  const suggeres = disponibles
    .filter((s) => !s.annonces && (s.dans_metiers ?? 0) > 0)
    .sort((a, b) => (b.dans_metiers ?? 0) - (a.dans_metiers ?? 0))
    .slice(0, SUGGESTIONS)

  const ligne = (e: LigneEmployeur) => (
    <LigneEmp
      key={e.id}
      e={e}
      onClick={() => navigate(`#/employeur/${e.id}`)}
      droite={
        <span className="emp-droite">
          {e.filtre && <BoutonFiltre filtre={e.filtre} onClick={async () => { if (await changer(e.id, e.nom)) onDone() }} />}
          {e.lu && <span className="when hide-sm">{t('emp.luQuand', { quand: relTime(e.lu) })}</span>}
          <SuivreSource source={parId.get(e.id)!} onDone={onDone} />
        </span>
      }
    />
  )

  return (
    <>
      {fenetre}
      {suivis.length === 0 && pages.length === 0 && <Empty>{t('sources.noneFollowed')}</Empty>}

      {employeurs.length > 0 && (
        <section className="acc-sec">
          <div className="acc-eyebrow">{t('emp.suivisTitre')}</div>
          <div className="acc-liste">{employeurs.map(ligne)}</div>
        </section>
      )}

      {pages.length > 0 && (
        <section className="acc-sec">
          <div className="acc-eyebrow">{plafond === null ? t('emp.enAttenteTitreSansMax') : t('emp.enAttenteTitre', { max: plafond })}</div>
          <div className="acc-liste">
            {pages.map((p) => <LignePage key={p.id} p={p} max={plafond ?? 0} court={false} />)}
          </div>
        </section>
      )}

      {annonces.length > 0 && (
        <section className="acc-sec">
          <div className="acc-eyebrow">{t('emp.annoncesTitre')}</div>
          <p className="field-help">{t('emp.annoncesAide')}</p>
          <div className="acc-liste">{annonces.map(ligne)}</div>
        </section>
      )}

      {suggeres.length > 0 && (
        <section className="acc-sec">
          <div className="acc-eyebrow">{t('emp.suggeresTitre')}</div>
          <div className="emp-sugg">
            {suggeres.map((s) => (
              <div className="card emp-sugg-c" key={s.id}>
                <LogoEntreprise nom={s.company ?? s.name} logo={s.logo} className="pc-logo acc-logo-sm" />
                <b>{s.company ?? s.name}</b>
                <span className="emp-sugg-n">
                  {s.dans_metiers === 1 ? t('emp.suggereUn') : t('emp.suggerePlus', { n: s.dans_metiers ?? 0 })}
                </span>
                <SuivreSource source={s} onDone={onDone} />
              </div>
            ))}
          </div>
        </section>
      )}
    </>
  )
}

/**
 * Corriger une source déclarée.
 *
 * L'ENTREPRISE EST LE CHAMP QUI COMPTE : c'est elle qu'affichent la veille, le
 * suivi et le nom des fichiers exportés. Une source ajoutée sans elle faisait
 * passer « jobs.eliagroup.eu » pour un employeur sur 220 offres, et rien ne
 * permettait de le corriger : on ne pouvait que mettre en pause.
 */
function EditerSource({
  source,
  onClose,
  onDone,
}: {
  source: SourceRow
  onClose: () => void
  onDone: () => void
}) {
  const [form, setForm] = useState({
    name: source.name,
    company: source.company ?? '',
    url: source.url,
  })
  const set = (k: keyof typeof form) => (e: any) => setForm({ ...form, [k]: e.target.value })
  const urlChangee = form.url.trim() !== source.url
  const toast = useToast()
  const t = useT()

  return (
    <Modal
      title={t('sources.editTitle', { name: source.company ?? source.name })}
      onClose={onClose}
      footer={
        <>
          <button className="btn-secondary" onClick={onClose}>{t('common.cancel')}</button>
          <ActionButton
            variant="primary"
            busyLabel={urlChangee ? t('sources.detecting2') : t('sources.saving')}
            onClick={async () => {
              await post(`/api/sources/${source.id}`, form)
              toast(t('sources.updatedToast'))
              onDone()
              onClose()
            }}
          >
            {t('sources.save')}
          </ActionButton>
        </>
      }
    >
      <ChangerLogo source={source} onDone={onDone} />
      <div className="field">
        <label className="field-label">{t('sources.companyLabel')}</label>
        <input className="input-field" type="text" value={form.company} onChange={set('company')} placeholder={source.name} />
        <div className="field-help">{t('sources.companyHelp')}</div>
      </div>
      <div className="field">
        <label className="field-label">{t('sources.sourceNameLabel')}</label>
        <input className="input-field" type="text" value={form.name} onChange={set('name')} />
        <div className="field-help">{t('sources.sourceNameHelp')}</div>
      </div>
      <div className="field">
        <label className="field-label">{t('sources.watchedUrlLabel')}</label>
        <input className="input-field" type="url" value={form.url} onChange={set('url')} />
        <div className="field-help">
          {urlChangee ? t('sources.urlWillRedetect') : t('sources.currentPlatform', { platform: source.platform })}
        </div>
      </div>
    </Modal>
  )
}

/**
 * Le logo d'une source, à la main quand la recherche automatique a trouvé un
 * favicon flou, celui de la plateforme, ou rien. Enregistré tout de suite,
 * indépendamment du reste de la fiche : c'est une image, pas un champ.
 */
function ChangerLogo({ source, onDone }: { source: SourceRow; onDone: () => void }) {
  const t = useT()
  const toast = useToast()
  const [logo, setLogo] = useState(source.logo)
  const [adresse, setAdresse] = useState('')
  const nom = source.company ?? source.name

  const envoyer = async (corps: Record<string, unknown>) => {
    try {
      const r = await post<SourceRow>(`/api/sources/${source.id}/logo`, corps)
      setLogo(r.logo)
      setAdresse('')
      toast(t('sources.logoUpdated'))
      onDone()
    } catch (e: any) {
      toast(e?.message ?? String(e), 'err')
    }
  }

  const fichier = (f: File | undefined) => {
    if (!f) return
    const lecteur = new FileReader()
    lecteur.onload = () => void envoyer({ dataUrl: String(lecteur.result) })
    lecteur.readAsDataURL(f)
  }

  return (
    <div className="field">
      <label className="field-label">{t('sources.logoLabel')}</label>
      <div className="logo-edit">
        <LogoEntreprise key={logo ?? ''} nom={nom} logo={logo} className="gs-logo" />
        <input
          className="input-field"
          type="url"
          value={adresse}
          onChange={(e) => setAdresse(e.target.value)}
          placeholder={t('sources.logoUrlPlaceholder')}
          aria-label={t('sources.logoUrlPlaceholder')}
        />
        <ActionButton size="sm" disabled={!adresse.trim()} onClick={() => envoyer({ url: adresse.trim() })}>
          {t('sources.logoSet')}
        </ActionButton>
      </div>
      <div className="logo-edit-act">
        <label className="chip">
          <Upload size={13} />
          {t('sources.logoFile')}
          <input type="file" accept="image/*" hidden onChange={(e) => fichier(e.target.files?.[0])} />
        </label>
        <ActionButton size="sm" onClick={() => envoyer({ auto: true })}>
          <RefreshCw size={13} />
          {t('sources.logoAuto')}
        </ActionButton>
      </div>
      <div className="field-help">{t('sources.logoHelp')}</div>
    </div>
  )
}

/**
 * Retirer une source. C'est un ARCHIVAGE, et la modale le dit.
 *
 * Le dépôt n'efface rien (principe n°2) : les offres de cette source sont
 * citées par des scores, des dossiers et des candidatures, donc les détruire
 * emporterait l'historique de ce qui a été envoyé à des clients. Annoncer
 * « supprimé » pour un archivage serait un mensonge d'interface ; annoncer
 * « archivé » sans dire ce que ça fait aux offres en serait un autre.
 */
function RetirerSource({
  source,
  onClose,
  onDone,
}: {
  source: SourceRow
  onClose: () => void
  onDone: () => void
}) {
  const toast = useToast()
  const t = useT()
  return (
    <Modal
      title={t('sources.removeConfirmTitle', { name: source.company ?? source.name })}
      onClose={onClose}
      footer={
        <>
          <button className="btn-secondary" onClick={onClose}>{t('common.cancel')}</button>
          <ActionButton
            variant="danger"
            busyLabel={t('sources.removing')}
            onClick={async () => {
              const r = await post<{ offers: number }>(`/api/sources/${source.id}/archive`, { archived: true })
              toast(t('sources.removedToast', { name: source.company ?? source.name, n: r.offers }))
              onDone()
              onClose()
            }}
          >
            <Trash2 size={15} />{t('sources.removeFromWatch')}
          </ActionButton>
        </>
      }
    >
      <p>{t('sources.removeConfirmP1', { n: source.offers })}</p>
      <p>{t('sources.removeConfirmP2')}</p>
    </Modal>
  )
}
